import { useMemo } from 'react';
import {
  type D2CPaymentTab,
  D2C_PAYMENT_TABS,
  D2C_PAYMENT_TAB_OPTIONS,
  getQuotePaymentOption,
} from '@shared/guidedPlanningPreferences';

import {
  type GuidedPlanningQuote,
  getPremiumAmount,
} from './use-quotes-display';

export interface D2CPaymentTabData {
  tab: D2CPaymentTab;
  quotes: GuidedPlanningQuote[];
}

interface UseD2CQuotesDisplayOptions {
  quotes: GuidedPlanningQuote[] | undefined;
}

interface UseD2CQuotesDisplayResult {
  tabs: D2CPaymentTabData[];
  populatedTabs: D2CPaymentTabData[];
  emptyTabs: D2CPaymentTabData[];
  defaultTab: D2CPaymentTab | undefined;
  totalCount: number;
}

/**
 * Groups quotes by payment frequency for the D2C tab view.
 * Within each tab, quotes are sorted by:
 *   1. Premium amount ascending (cheapest first)
 *   2. ROI descending (best value first when premiums are equal)
 */
export function useD2CQuotesDisplay({
  quotes,
}: UseD2CQuotesDisplayOptions): UseD2CQuotesDisplayResult {
  return useMemo(() => {
    if (!quotes || quotes.length === 0) {
      const emptyTabs = D2C_PAYMENT_TABS.map(tab => ({ tab, quotes: [] }));
      return {
        tabs: emptyTabs,
        populatedTabs: [],
        emptyTabs,
        defaultTab: undefined,
        totalCount: 0,
      };
    }

    const tabQuotesMap = new Map<D2CPaymentTab, GuidedPlanningQuote[]>();
    for (const tab of D2C_PAYMENT_TABS) {
      tabQuotesMap.set(tab, []);
    }

    for (const quote of quotes) {
      const paymentOption = getQuotePaymentOption(quote);

      for (const tab of D2C_PAYMENT_TABS) {
        if (D2C_PAYMENT_TAB_OPTIONS[tab].includes(paymentOption)) {
          tabQuotesMap.get(tab)!.push(quote);
          break;
        }
      }
    }

    const tabs: D2CPaymentTabData[] = D2C_PAYMENT_TABS.map(tab => ({
      tab,
      quotes: tabQuotesMap.get(tab)!.sort((a, b) => {
        const premiumDiff = getPremiumAmount(a) - getPremiumAmount(b);
        if (premiumDiff !== 0) return premiumDiff;
        return (b.roi ?? 0) - (a.roi ?? 0);
      }),
    }));

    const populatedTabs = tabs.filter(t => t.quotes.length > 0);
    const emptyTabs = tabs.filter(t => t.quotes.length === 0);
    const defaultTab = populatedTabs[0]?.tab;

    return {
      tabs,
      populatedTabs,
      emptyTabs,
      defaultTab,
      totalCount: quotes.length,
    };
  }, [quotes]);
}
